import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, MapPin } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function photoUrl(u) { if (!u) return null; return u.startsWith("http") ? u : `${BACKEND_URL}${u}`; }

function relativeTime(iso, t) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = Math.floor((now - then) / 60000);
    if (diffMin < 1) return t("app.notifications.justNow");
    if (diffMin < 60) return t("app.notifications.minutesAgo", { n: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return t("app.notifications.hoursAgo", { n: diffH });
    const diffD = Math.floor(diffH / 24);
    return t("app.notifications.daysAgo", { n: diffD });
  } catch { return ""; }
}

function ConversationRow({ conv, t }) {
  const other = conv.other || {};
  const img = photoUrl(other.main_photo_url);
  const last = conv.last_message;
  const isUnread = conv.unread_count > 0;
  const preview = last
    ? (last.kind === "voice"
        ? `${last.is_mine ? t("app.messaging.you") + ": " : ""}🎙 ${t("app.messaging.voiceMessage")}`
        : last.kind === "photo"
        ? `${last.is_mine ? t("app.messaging.you") + ": " : ""}📷 ${t("app.messaging.photo")}`
        : (last.is_mine ? `${t("app.messaging.you")}: ${last.body}` : last.body))
    : t("app.match.matchedRecently");
  return (
    <Link
      to={`/app/messages/${conv.match_id}`}
      data-testid={`conversation-${conv.match_id}`}
      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${isUnread ? "bg-[hsl(var(--muted))]" : "hover:bg-[hsl(var(--muted))]"}`}
      style={{ borderColor: "hsl(var(--nvl-border))" }}
    >
      <div
        className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-display text-xl"
        style={{
          backgroundImage: img ? `url(${img})` : undefined,
          backgroundSize: "cover", backgroundPosition: "center",
          backgroundColor: "hsl(var(--muted))",
          color: "hsl(var(--nvl-accent))",
        }}
      >
        {img ? null : (other.display_name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`truncate ${isUnread ? "font-semibold" : "font-medium"}`}>{other.display_name}</p>
          <span className="text-[10px] flex-shrink-0" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {last ? relativeTime(last.created_at, t) : relativeTime(conv.match_created_at, t)}
          </span>
        </div>
        <p className={`text-xs mt-0.5 truncate ${isUnread ? "" : "opacity-70"}`} style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {preview}
        </p>
      </div>
      {isUnread ? (
        <span
          className="text-[10px] px-2 py-0.5 rounded-full"
          style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
          data-testid={`conversation-unread-${conv.match_id}`}
        >
          {conv.unread_count > 9 ? "9+" : conv.unread_count}
        </span>
      ) : null}
    </Link>
  );
}

export default function MessagesInbox() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setItems(data.conversations || []);
    } catch (e) {
      setError(extractError(e, t("app.messaging.loadingError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="max-w-3xl mx-auto" data-testid="page-messages-inbox">
      <div>
        <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
          <MessageCircle size={12} className="inline mr-1 -mt-0.5" /> {t("app.messaging.inboxTitle")}
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-display">{t("app.messaging.inboxTitle")}</h1>
        <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{t("app.messaging.inboxSubtitle")}</p>
      </div>

      <div className="mt-8 space-y-2">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse h-20 rounded-lg" style={{ backgroundColor: "hsl(var(--muted))" }} />
          ))
        ) : error ? (
          <div className="nvl-card p-6" data-testid="inbox-error">
            <p className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="nvl-card p-10 text-center" data-testid="inbox-empty">
            <MessageCircle size={22} style={{ color: "hsl(var(--nvl-accent))" }} className="mx-auto" />
            <h3 className="mt-4 font-display text-xl">{t("app.messaging.inboxEmpty")}</h3>
            <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{t("app.messaging.inboxEmptyBody")}</p>
            <Link
              to="/app/matches"
              className="mt-4 inline-block nvl-btn nvl-btn-outline"
              data-testid="inbox-view-matches"
            >
              {t("app.match.title")}
            </Link>
          </div>
        ) : (
          items.map((c) => <ConversationRow key={c.match_id} conv={c} t={t} />)
        )}
      </div>
    </div>
  );
}
